import { useEffect, useState } from "react";
import { api } from "@/lib/config";
import { CheckCircle2, AlertTriangle, RefreshCw, MessageCircle } from "lucide-react";

export default function EmailLog({ secret }) {
  const headers = { "X-Admin-Secret": secret };
  const [data, setData] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/emails", { headers });
    setData(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!data) return <p className="mt-8 text-[#23170E]/55">Loading…</p>;

  const onSandbox = data.sender.includes("resend.dev");

  return (
    <div data-testid="admin-email-log" className="mt-8">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat k="Sending from" v={data.sender} />
        <Stat k="Bookings land in" v={data.business_email} />
        <Stat k="Mail provider" v={data.provider === "hostinger-smtp" ? "Hostinger SMTP" : "Resend"} />
        <div className="bg-[#FFFFFF] border border-[#E2D5C3] rounded-3xl shadow-[0_4px_20px_-2px_rgba(35,23,14,0.06)] p-6">
          <p className="overline">Failed sends</p>
          <p data-testid="email-failed-count" className="font-marcellus text-4xl text-[#A6521B] mt-2">{data.failed}</p>
        </div>
      </div>

      {onSandbox && (
        <Notice testid="email-domain-warning" title="Guest confirmations are not reaching guests yet">
          The site is falling back to Resend's shared test address, which only delivers to your own inbox.
          Any confirmation that can't reach a guest is forwarded to you marked
          <span className="font-mono text-xs"> [UNDELIVERED — forward to guest]</span> so nothing is lost.
        </Notice>
      )}

      {/* WhatsApp instant alerts */}
      <div data-testid="whatsapp-alert-status" className="mt-5 rounded-3xl border border-[#E2D5C3] bg-[#FFFFFF] p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="overline flex items-center gap-2"><MessageCircle size={14} className="text-[#25D366]" /> WhatsApp booking alerts</p>
            <p className="mt-2 text-sm text-[#23170E]/75">
              {data.whatsapp_enabled
                ? <>Active — every new booking and custom trip pings <strong>{(data.whatsapp_recipients || [data.whatsapp_phone]).join(", ")}</strong> instantly.</>
                : <>Not active yet. Bookings still reach you by email immediately.</>}
            </p>
          </div>
          <span className={`text-xs uppercase tracking-[0.15em] px-3 py-1.5 rounded-full ${data.whatsapp_enabled ? "bg-emerald-100 text-emerald-800" : "bg-[#A6521B]/12 text-[#A6521B]"}`}>
            {data.whatsapp_enabled ? "Active" : "Needs one-time setup"}
          </span>
        </div>

        {data.whatsapp_enabled && (
          <p className="mt-4 text-xs text-[#23170E]/60 leading-relaxed">
            Want alerts on another phone too? That number must get its own CallMeBot key: save
            <span className="font-mono"> +34 644 51 95 23</span>, send <span className="font-mono">I allow callmebot to send me messages</span> from
            the new phone, then pass the returned key and number to your developer.
          </p>
        )}

        {!data.whatsapp_enabled && (
          <ol className="mt-5 space-y-2 text-sm text-[#23170E]/80 list-decimal pl-5 leading-relaxed">
            <li>Save <span className="font-mono text-xs">+34 644 51 95 23</span> in your phone as a contact (CallMeBot).</li>
            <li>From <strong>{data.whatsapp_phone || "your WhatsApp number"}</strong>, send it exactly: <span className="font-mono text-xs">I allow callmebot to send me messages</span></li>
            <li>It replies with a personal API key — send that key to your developer and alerts switch on immediately.</li>
          </ol>
        )}

        {data.whatsapp_alerts?.length > 0 && (
          <div className="mt-5 border-t border-[#E2D5C3] pt-4 space-y-2">
            {data.whatsapp_alerts.slice(0, 5).map((w) => (
              <div key={w.id} className="flex items-start gap-3 text-xs">
                <span className={w.status === "sent" ? "text-emerald-700" : "text-[#A6352B]"}>{w.status}</span>
                <span className="text-[#23170E]/70 whitespace-pre-line flex-1">{w.text.split("\n")[0]}</span>
                <span className="text-[#23170E]/45 whitespace-nowrap">{new Date(w.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <h3 className="font-marcellus text-2xl">Recent sends</h3>
        <button data-testid="email-refresh-btn" onClick={load} className="text-sm text-[#A6521B] flex items-center gap-1.5">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      <div className="mt-4 overflow-x-auto bg-[#FFFFFF] rounded-3xl border border-[#E2D5C3] p-2">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-[0.15em] text-[#23170E]/55 border-b border-[#BCA993]">
            <tr>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4">Via</th>
              <th className="py-3 px-4">To</th>
              <th className="py-3 px-4">Subject</th>
              <th className="py-3 px-4">Sent</th>
            </tr>
          </thead>
          <tbody>
            {data.emails.map((e) => (
              <tr key={e.id} className="border-b border-[#E2D5C3]">
                <td className="py-3 px-4">
                  {e.status === "sent" ? (
                    <span className="inline-flex items-center gap-1.5 text-emerald-700 text-xs uppercase tracking-[0.1em]"><CheckCircle2 size={13} /> Sent</span>
                  ) : (
                    <span title={e.error || ""} className="inline-flex items-center gap-1.5 text-[#A6352B] text-xs uppercase tracking-[0.1em]"><AlertTriangle size={13} /> {e.status}</span>
                  )}
                </td>
                <td className="py-3 px-4 text-xs text-[#23170E]/60">{e.provider === "hostinger-smtp" ? "Hostinger" : e.provider === "resend-fallback" ? "Resend" : "—"}</td>
                <td className="py-3 px-4 break-all">{e.to}</td>
                <td className="py-3 px-4">
                  {e.subject}
                  {e.error && <div className="text-xs text-[#A6352B]/80 mt-1">{e.error}</div>}
                </td>
                <td className="py-3 px-4 text-xs text-[#23170E]/55 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
              </tr>
            ))}
            {data.emails.length === 0 && (
              <tr><td colSpan={5} className="py-12 text-center text-[#23170E]/55">No emails sent yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="bg-[#FFFFFF] border border-[#E2D5C3] rounded-3xl shadow-[0_4px_20px_-2px_rgba(35,23,14,0.06)] p-6">
      <p className="overline">{k}</p>
      <p className="mt-2 text-sm break-all">{v}</p>
    </div>
  );
}

function Notice({ title, children, testid }) {
  return (
    <div data-testid={testid} className="mt-5 rounded-3xl border border-[#A6521B]/35 bg-[#A6521B]/8 p-6 text-sm leading-relaxed">
      <p className="font-semibold flex items-center gap-2 text-[#A6352B]"><AlertTriangle size={16} /> {title}</p>
      <p className="mt-2 text-[#23170E]/80">{children}</p>
    </div>
  );
}
